import useArchiveDocumentTemplate from '@/api/mutations/documentTemplate/useArchiveDocumentTemplate';
import useGetMyAccountDocumentTemplates, {
  DocumentTemplate,
} from '@/api/queries/me/useGetMyAccountDocumentTemplates';
import { useUserHasPermission } from '@/hooks/usePermissions';
import {
  Button,
  ManageableItemsList,
  Modal,
  ModalProps,
  Tooltip,
} from '@payaca/ambrosia';

import { DocumentTemplatePermissions } from '@payaca/permissions/documentTemplate/documentTemplate.permissions';
import { checkUnreachable } from '@payaca/utilities/guards';
import { produce } from 'immer';
import { FC, useReducer, useState } from 'react';
import { useHistory } from 'react-router';
import { usePaginationState } from '../projectPage/components/MaterialsPanel/materialsPanelHooks';

export const DocumentTemplatesList: FC = () => {
  const { pagination, setCurrentPage, ...paginationActions } =
    usePaginationState(100);
  const [searchTermInput, setSearchTermInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const { data: documentTemplatesPage, isLoading } =
    useGetMyAccountDocumentTemplates({
      pagination,
      input: searchTerm ? { searchTerm } : undefined,
    });
  const history = useHistory();

  const canCreate = useUserHasPermission({
    permissions: [DocumentTemplatePermissions.CREATE_DOCUMENT_TEMPLATE],
  });

  const canEdit = useUserHasPermission({
    permissions: [DocumentTemplatePermissions.UPDATE_DOCUMENT_TEMPLATE],
  });

  const canArchive = useUserHasPermission({
    permissions: [DocumentTemplatePermissions.ARCHIVE_DOCUMENT_TEMPLATE],
  });

  const [state, dispatch] = useReducer(documentTemplatesReducer, {
    openDialog: null,
  });

  return (
    <div className="p-4">
      <ManageableItemsList>
        <ManageableItemsList.ActionBar>
          <ManageableItemsList.ActionBar.SearchInput
            value={searchTermInput}
            onChange={setSearchTermInput}
            changeTimeoutMs={300}
            onChangeTimeout={(value: string) => {
              setSearchTerm(value);
              setCurrentPage(1);
            }}
          />
          <Tooltip
            enabled={!canCreate}
            tooltipContent="You don't have permission to perform this action"
          >
            <Button
              size="sm"
              disabled={!canCreate}
              onClick={
                canCreate
                  ? () => history.push('/templates/document-templates/new')
                  : undefined
              }
            >
              Create document template
            </Button>
          </Tooltip>
        </ManageableItemsList.ActionBar>
        <ManageableItemsList.Table
          emptyText={
            searchTerm
              ? `No templates match "${searchTerm}"`
              : 'No templates have been added yet'
          }
          uniqueKey="id"
          items={documentTemplatesPage?.items || []}
          isLoading={isLoading}
          onClickRow={
            canEdit
              ? (item) =>
                  history.push(`/templates/document-templates/edit/${item.id}`)
              : undefined
          }
          itemActions={[
            {
              label: 'Edit',
              isDisabled: () => !canEdit,
              disabledReasons: [
                "You don't have permission to perform this action",
              ],
              onClick: (item) =>
                history.push(`/templates/document-templates/edit/${item.id}`),
            },
            {
              label: 'Archive',
              isDisabled: () => !canArchive,
              disabledReasons: [
                "You don't have permission to perform this action",
              ],
              onClick: (item) => {
                dispatch({
                  type: 'openConfirmArchiveDocumentTemplateDialog',
                  selectedTemplate: item,
                });
              },
            },
          ]}
        >
          <ManageableItemsList.Table.Column header="Name" field="name" />
          <ManageableItemsList.Table.Column
            header="Description"
            field="description"
          />
        </ManageableItemsList.Table>
        <ManageableItemsList.PaginationBar
          type="from-paginated-result"
          {...documentTemplatesPage}
          onPageChange={setCurrentPage}
        />
        {state.selectedTemplate && (
          <ArchiveTemplateModal
            isOpen={state.openDialog === 'archive'}
            onClose={() => dispatch({ type: 'closeDialog' })}
            template={state.selectedTemplate}
          />
        )}
      </ManageableItemsList>
    </div>
  );
};

type Props = {
  template: DocumentTemplate;
} & Pick<ModalProps, 'isOpen' | 'onClose'>;
const ArchiveTemplateModal: FC<Props> = (props) => {
  const { isOpen, onClose, template } = props;

  const { mutateAsync, isPending: isDeleting } = useArchiveDocumentTemplate();

  const handleDelete = async () => {
    await mutateAsync({ id: template.id }).then(onClose);
  };

  return (
    <Modal
      title={`Archive document template`}
      isOpen={isOpen}
      onClose={onClose}
    >
      <Modal.Body>
        <p className="mb-6 text-base">
          Are you sure you want to archive this document template?
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Actions>
          <Button
            className="mr-2"
            variant="secondary"
            disabled={isDeleting}
            onClick={onClose}
          >
            Keep
          </Button>
          <Button
            isProcessing={isDeleting}
            disabled={isDeleting}
            colour="negative"
            onClick={handleDelete}
          >
            Delete
          </Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Modal>
  );
};

type DocumentTemplatesState = {
  openDialog?: 'archive' | null;
  selectedTemplate?: DocumentTemplate;
};

type DocumentTemplatesAction =
  | {
      type: 'openConfirmArchiveDocumentTemplateDialog';
      selectedTemplate: DocumentTemplate;
    }
  | { type: 'closeDialog' };

const documentTemplatesReducer = (
  baseState: DocumentTemplatesState,
  action: DocumentTemplatesAction
) => {
  return produce(baseState, (draftState) => {
    const type = action.type;

    switch (type) {
      case 'openConfirmArchiveDocumentTemplateDialog':
        draftState.openDialog = 'archive';
        draftState.selectedTemplate = action.selectedTemplate;
        break;
      case 'closeDialog':
        draftState.openDialog = null;
        draftState.selectedTemplate = undefined;
        break;
      default:
        checkUnreachable(type);
        break;
    }
  });
};
